<script setup>
import { computed, onMounted, ref } from "vue";
import {
  fetchComplaints,
  fetchComplaintsByUser,
  fetchAdmins,
  getMediaUrl,
  rerunComplaintAi,
} from "@/services/backofficeClient";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { ROLES } from "@/services/acl";

const STATUS_OPTIONS = ["pending", "resolved", "rejected"];
const SORT_OPTIONS = [
  { value: "lastCreatedAt", label: "Latest complaint" },
  { value: "firstCreatedAt", label: "First complaint" },
  { value: "count", label: "Complaint count" },
];
const DEFAULT_PAGE_SIZE = 20;

const defaultSortByForGroup = (value) =>
  value === "reporterUserId" ? "count" : "firstCreatedAt";

const complaints = ref([]);
const isLoading = ref(true);
const errorMessage = ref("");
const statusFilter = ref("pending");
const groupBy = ref("reportedUserId");
const sortBy = ref(defaultSortByForGroup(groupBy.value));
const currentPage = ref(1);
const pagination = ref({
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalItems: 0,
  totalPages: 1,
  hasNext: false,
});

const showMediaModal = ref(false);
const currentMediaUrl = ref("");
const router = useRouter();
const store = useStore();
const canEditComplaint = computed(() => store.getters.hasRole(ROLES.COMPLAINT_EDIT));

const isRerunningAi = ref(false);
const bulkActionMessage = ref("");
const bulkActionType = ref("success");

// ---------------------------------------------------------------------------
// Admins (loaded once on mount, kept in memory)
// ---------------------------------------------------------------------------
const admins = ref([]);
const adminsById = computed(() => {
  const map = {};
  admins.value.forEach((admin) => {
    map[admin.id] = admin;
  });
  return map;
});
const currentAdminId = computed(() => store.getters.currentAdmin?.id || null);

const loadAdmins = async () => {
  try {
    const payload = await fetchAdmins({ pageSize: 200 });
    const items = Array.isArray(payload) ? payload : payload?.items;
    admins.value = (items || []).map((admin) => ({
      id: admin.id,
      name: admin.name || admin.email || admin.id,
    }));
  } catch (err) {
    console.error("Failed to load admins", err);
  }
};

const adminLabel = (adminId) => {
  if (!adminId) return "Unassigned";
  return adminsById.value[adminId]?.name || adminId;
};

// Assigned-admin filter: array of selected values, where `null` means "Unassigned".
// Defaults to Unassigned + complaints assigned to the current admin.
const defaultAssignedAdminIds = computed(() => {
  const ids = [null];
  if (currentAdminId.value) ids.push(currentAdminId.value);
  return ids;
});
const filterAssignedAdminIds = ref([...defaultAssignedAdminIds.value]);
const assignedFilterMenuOpen = ref(false);

const isAssignedFilterDefault = computed(() => {
  const current = new Set(filterAssignedAdminIds.value.map((v) => v ?? "null"));
  const def = new Set(defaultAssignedAdminIds.value.map((v) => v ?? "null"));
  if (current.size !== def.size) return false;
  for (const v of current) {
    if (!def.has(v)) return false;
  }
  return true;
});

const isAssignedFilterSelected = (value) =>
  filterAssignedAdminIds.value.includes(value);

const toggleAssignedFilterValue = (value) => {
  if (isAssignedFilterSelected(value)) {
    filterAssignedAdminIds.value = filterAssignedAdminIds.value.filter(
      (v) => v !== value
    );
  } else {
    filterAssignedAdminIds.value = [...filterAssignedAdminIds.value, value];
  }
  currentPage.value = 1;
  loadComplaints();
};

const toggleAssignedFilterMenu = () => {
  assignedFilterMenuOpen.value = !assignedFilterMenuOpen.value;
};

const assignedFilterLabel = computed(() => {
  if (filterAssignedAdminIds.value.length === 0) return "All";
  return filterAssignedAdminIds.value
    .map((v) => (v === currentAdminId.value && v !== null ? "Me" : adminLabel(v)))
    .join(", ");
});

const normalizedPageSize = computed(() => {
  const size = Number(pagination.value.pageSize);
  return Number.isFinite(size) && size > 0 ? size : DEFAULT_PAGE_SIZE;
});
const totalItems = computed(() => {
  const total = Number(pagination.value.totalItems);
  return Number.isFinite(total) && total >= 0 ? total : 0;
});
const totalPages = computed(() => {
  const pages = Number(pagination.value.totalPages);
  return Number.isFinite(pages) && pages > 0 ? pages : 1;
});
const rangeStart = computed(() => {
  if (totalItems.value === 0) return 0;
  return (Math.max(1, currentPage.value) - 1) * normalizedPageSize.value + 1;
});
const rangeEnd = computed(() => {
  if (totalItems.value === 0) return 0;
  return Math.min(currentPage.value * normalizedPageSize.value, totalItems.value);
});
const canGoPrev = computed(() => currentPage.value > 1 && !isLoading.value);
const canGoNext = computed(
  () => currentPage.value < totalPages.value && !isLoading.value
);
const isEmpty = computed(
  () => !isLoading.value && !errorMessage.value && complaints.value.length === 0
);

const isGroupedByReporter = computed({
  get: () => groupBy.value === "reporterUserId",
  set: (value) => {
    const nextGroup = value ? "reporterUserId" : "reportedUserId";
    groupBy.value = nextGroup;
    sortBy.value = defaultSortByForGroup(nextGroup);
  },
});

const groupedUserLabel = computed(() =>
  groupBy.value === "reporterUserId" ? "Reporter User" : "Reported User"
);

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = parseDate(value);
  if (!date) return "--";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const parseComplaintAggregate = (item) => {
  const user = item.user || {};
  const photoAtoms = Array.isArray(item.user?.atoms)
    ? item.user.atoms.filter((atom) => atom?.attribute === "photo")
    : [];
  const photos = photoAtoms
    .map((atom) => atom?.mediaPath || atom?.media_path)
    .filter(Boolean)
    .map((path) => path.toString());
  return {
    id: item.id || "",
    complaintCount: Number(item.complaintCount) || 0,
    status: item.status || "--",
    user: {
      id: user.id ? String(user.id) : "",
      name:
        user.name ||
        user.fullName ||
        user.displayName ||
        [user.firstName, user.lastName]
          .filter(Boolean)
          .map((v) => String(v))
          .join(" ")
          .trim() ||
        "Unknown",
      status: user.status || user.state || "--",
    },
    photoPaths: photos,
    createdAt: user.createdAt,
  };
};

const resolvePagination = (rawPagination, itemsLength) => {
  const fallbackTotalPages = Math.max(
    1,
    Math.ceil((itemsLength || 0) / DEFAULT_PAGE_SIZE)
  );
  if (rawPagination && typeof rawPagination === "object") {
    const page = Number(rawPagination.page) || currentPage.value;
    const pageSize = Number(rawPagination.pageSize) || DEFAULT_PAGE_SIZE;
    const totalItemsValue =
      Number(rawPagination.totalItems) || Number(itemsLength) || 0;
    const totalPagesValue =
      Number(rawPagination.totalPages) || fallbackTotalPages;
    return {
      page: page > 0 ? page : 1,
      pageSize: pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE,
      totalItems: totalItemsValue >= 0 ? totalItemsValue : 0,
      totalPages: totalPagesValue > 0 ? totalPagesValue : fallbackTotalPages,
      hasNext:
        rawPagination.hasNext === true
          ? true
          : (page || 1) < (totalPagesValue || fallbackTotalPages),
    };
  }
  return {
    page: currentPage.value,
    pageSize: DEFAULT_PAGE_SIZE,
    totalItems: itemsLength,
    totalPages: fallbackTotalPages,
    hasNext: fallbackTotalPages > currentPage.value,
  };
};

const loadComplaints = async () => {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    const payload = await fetchComplaints({
      page: currentPage.value,
      pageSize: DEFAULT_PAGE_SIZE,
      status: statusFilter.value || undefined,
      groupBy: groupBy.value,
      sortBy: sortBy.value,
      assignedAdminIds: filterAssignedAdminIds.value,
    });

    const rawItems = Array.isArray(payload) ? payload : payload?.items;
    const parsed = Array.isArray(rawItems)
      ? rawItems
        .filter((item) => item && typeof item === "object")
        .map((item) => parseComplaintAggregate(item))
      : [];

    const meta = resolvePagination(payload?.pagination, parsed.length);
    pagination.value = meta;
    currentPage.value = Math.min(
      Math.max(1, meta.page || 1),
      Math.max(1, meta.totalPages || 1)
    );
    complaints.value = parsed;
  } catch (error) {
    console.error("Failed to fetch complaints", error);
    errorMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load complaints. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

const rerunAiForShownComplaints = async () => {
  if (isRerunningAi.value || isLoading.value || statusFilter.value !== "pending") {
    return;
  }
  const rows = complaints.value.filter((aggregate) => aggregate.user?.id);
  if (rows.length === 0) return;

  isRerunningAi.value = true;
  bulkActionMessage.value = "";
  try {
    const complaintLists = await Promise.all(
      rows.map((row) =>
        fetchComplaintsByUser(row.user.id, { groupBy: groupBy.value }).catch(
          () => []
        )
      )
    );
    const eligible = complaintLists
      .flat()
      .filter(
        (complaint) =>
          complaint &&
          complaint.status === "pending" &&
          complaint.reporterType === "user" &&
          complaint.reporterUserId
      );

    if (eligible.length === 0) {
      bulkActionMessage.value = "No eligible pending complaints to rerun.";
      bulkActionType.value = "warning";
      return;
    }

    const results = await Promise.allSettled(
      eligible.map((complaint) => rerunComplaintAi(complaint.id))
    );
    const succeeded = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - succeeded;
    bulkActionMessage.value = failed
      ? `Reran AI for ${succeeded} complaint(s), ${failed} failed.`
      : `Reran AI for ${succeeded} complaint(s).`;
    bulkActionType.value = failed ? "warning" : "success";
    if (succeeded > 0) {
      await loadComplaints();
    }
  } catch (error) {
    console.error("Failed to rerun AI for shown complaints", error);
    bulkActionMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to rerun AI for shown complaints.";
    bulkActionType.value = "danger";
  } finally {
    isRerunningAi.value = false;
  }
};

const changePage = (delta) => {
  const nextPage = currentPage.value + delta;
  if (nextPage < 1 || nextPage === currentPage.value) return;
  currentPage.value = nextPage;
  loadComplaints();
};

const resetFilters = () => {
  statusFilter.value = "";
  groupBy.value = "reportedUserId";
  sortBy.value = defaultSortByForGroup(groupBy.value);
  filterAssignedAdminIds.value = [...defaultAssignedAdminIds.value];
  currentPage.value = 1;
  loadComplaints();
};

const openMedia = (url) => {
  if (!url) return;
  currentMediaUrl.value = url;
  showMediaModal.value = true;
};

const closeMedia = () => {
  showMediaModal.value = false;
  currentMediaUrl.value = "";
};

const buildNavQuery = () => {
  const query = {};
  if (statusFilter.value) query.status = statusFilter.value;
  if (sortBy.value) query.sortBy = sortBy.value;
  if (filterAssignedAdminIds.value.length) {
    query.assignedAdminIds = filterAssignedAdminIds.value.map((v) =>
      v === null ? "null" : v
    );
  }
  return Object.keys(query).length ? query : undefined;
};

const goToDetail = (aggregate) => {
  if (!aggregate?.user?.id || isLoading.value) return;
  const segment = groupBy.value === "reporterUserId" ? "reporter" : "reported";
  router.push({
    path: `/complaints/${segment}/${aggregate.user.id}`,
    query: buildNavQuery(),
  });
};

onMounted(() => {
  loadAdmins();
  loadComplaints();
});
</script>

<template>
  <div class="py-4 container-fluid">

    <div class="card">
      <div class="card-header pb-0 border-0">
        <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
          <div>
            <h6 class="mb-1">Complaints</h6>
            <p class="text-xs text-secondary mb-0">
              Page {{ currentPage }} of {{ totalPages }} · {{ totalItems }} total
            </p>
          </div>
          <div class="mt-3 mt-lg-0 d-flex align-items-center gap-2 flex-wrap">
            <button class="btn btn-sm btn-outline-secondary mb-0" type="button" :disabled="isLoading"
              @click="resetFilters">
              Reset filters
            </button>
            <button class="btn btn-sm btn-outline-success mb-0" type="button" :disabled="isLoading"
              @click="loadComplaints">
              <i class="fas fa-sync me-2"></i>
              {{ isLoading ? "Refreshing..." : "Refresh" }}
            </button>
            <button v-if="canEditComplaint" class="btn btn-sm btn-outline-warning mb-0" type="button"
              :disabled="isLoading || isRerunningAi || statusFilter !== 'pending' || complaints.length === 0"
              :title="statusFilter !== 'pending' ? 'Filter by Pending status to enable' : ''"
              @click="rerunAiForShownComplaints">
              <span v-if="isRerunningAi" class="spinner-border spinner-border-sm me-2"></span>
              {{ isRerunningAi ? "Rerunning AI..." : "Rerun AI for shown pending complaints" }}
            </button>
          </div>
        </div>

        <div v-if="bulkActionMessage" class="alert mt-3 py-2" :class="`alert-${bulkActionType}`">
          {{ bulkActionMessage }}
        </div>

        <div class="row g-2 align-items-center mt-3">
          <div class="col-12 col-lg-3">
            <label class="form-label text-sm fw-semibold">Status filter</label>
            <select class="form-select form-select-sm" v-model="statusFilter" :disabled="isLoading"
              @change="() => { currentPage = 1; loadComplaints(); }">
              <option value="">All statuses</option>
              <option v-for="status in STATUS_OPTIONS" :key="status" :value="status">
                {{ status.charAt(0).toUpperCase() + status.slice(1) }}
              </option>
            </select>
          </div>
          <div class="col-12 col-lg-3">
            <label class="form-label text-sm fw-semibold">Group by</label>
            <div class="form-check form-switch">
              <input class="form-check-input" type="checkbox" id="complaints-group-by-reporter"
                v-model="isGroupedByReporter" :disabled="isLoading"
                @change="() => { currentPage = 1; loadComplaints(); }" />
              <label class="form-check-label text-sm" for="complaints-group-by-reporter">
                {{ isGroupedByReporter ? "Reporter user" : "Reported user" }}
              </label>
            </div>
          </div>
          <div class="col-12 col-lg-3">
            <label class="form-label text-sm fw-semibold">Sort by</label>
            <select class="form-select form-select-sm" v-model="sortBy" :disabled="isLoading"
              @change="() => { currentPage = 1; loadComplaints(); }">
              <option v-for="option in SORT_OPTIONS" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
          <div class="col-12 col-lg-3">
            <label class="form-label text-sm fw-semibold">Assigned</label>
            <div class="position-relative">
              <button class="btn btn-sm w-100 text-truncate mb-0"
                :class="isAssignedFilterDefault ? 'btn-outline-primary' : 'btn-primary'"
                @click="toggleAssignedFilterMenu" title="Filter by assigned admin">
                {{ assignedFilterLabel }}
              </button>
              <div v-if="assignedFilterMenuOpen"
                class="position-absolute bg-white shadow-sm rounded-2 border py-1 assign-filter-dropdown">
                <label class="dropdown-item text-sm py-1 px-2 d-flex align-items-center gap-2 mb-0"
                  style="cursor:pointer">
                  <input type="checkbox" :checked="isAssignedFilterSelected(null)"
                    @change="toggleAssignedFilterValue(null)" />
                  Unassigned
                </label>
                <label v-if="currentAdminId"
                  class="dropdown-item text-sm py-1 px-2 d-flex align-items-center gap-2 mb-0" style="cursor:pointer">
                  <input type="checkbox" :checked="isAssignedFilterSelected(currentAdminId)"
                    @change="toggleAssignedFilterValue(currentAdminId)" />
                  Me ({{ store.getters.currentAdmin?.name }})
                </label>
                <div v-if="admins.length" class="dropdown-divider my-1"></div>
                <label v-for="admin in admins.filter((a) => a.id !== currentAdminId)" :key="admin.id"
                  class="dropdown-item text-sm py-1 px-2 d-flex align-items-center gap-2 mb-0" style="cursor:pointer">
                  <input type="checkbox" :checked="isAssignedFilterSelected(admin.id)"
                    @change="toggleAssignedFilterValue(admin.id)" />
                  {{ admin.name }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="card-body text-center py-5">
        <div class="spinner-border text-success" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3 mb-0 text-sm text-secondary">Loading complaints...</p>
      </div>

      <div v-else-if="errorMessage" class="card-body text-center py-5">
        <div class="icon icon-shape bg-gradient-danger text-white shadow text-center border-radius-md mx-auto">
          <i class="ni ni-fat-remove"></i>
        </div>
        <p class="mt-3 text-sm">{{ errorMessage }}</p>
        <button class="btn btn-sm btn-success" type="button" @click="loadComplaints">
          Try again
        </button>
      </div>

      <div v-else-if="isEmpty" class="card-body text-center py-5">
        <div class="icon icon-shape bg-gradient-success text-white shadow text-center border-radius-md mx-auto">
          <i class="ni ni-check-bold"></i>
        </div>
        <p class="mt-3 text-sm">No complaints found.</p>
      </div>

      <div v-else class="card-body shadow-sm">
        <div class="table-responsive">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  {{ groupedUserLabel }}
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Status
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Photos
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                  Complaints
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                  User Created
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="aggregate in complaints" :key="aggregate.id || aggregate.user.id"
                @click="goToDetail(aggregate)" :style="aggregate.user.id ? 'cursor: pointer' : ''">
                <td>
                  <div class="d-flex flex-column">
                    <span class="text-sm font-weight-bold">
                      {{ aggregate.user.name || "--" }}
                    </span>
                    <span class="text-xs text-secondary">
                      {{ aggregate.user.id || "" }}
                    </span>
                  </div>
                </td>
                <td>
                  <span class="badge bg-light text-dark border text-xxs">
                    {{ aggregate.user.status || "--" }}
                  </span>
                </td>
                <td>
                  <div class="d-flex flex-wrap gap-2">
                    <template v-for="path in aggregate.photoPaths" :key="path">
                      <img v-if="getMediaUrl(path)" :src="getMediaUrl(path)" alt="complaint photo" class="rounded"
                        style="width: 48px; height: 48px; object-fit: cover; cursor: zoom-in"
                        @click="openMedia(getMediaUrl(path))" />
                    </template>
                    <span v-if="!aggregate.photoPaths.length" class="text-sm text-secondary">--</span>
                  </div>
                </td>
                <td class="text-center">
                  <span class="text-sm font-weight-bold">
                    {{ aggregate.complaintCount }}
                  </span>
                </td>
                <td class="text-center">
                  <span class="text-sm">
                    {{ formatDate(aggregate.createdAt) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex align-items-center flex-wrap gap-2 pt-3 border-top">
          <p class="mb-0 text-sm">
            {{
              totalItems === 0
                ? "No results"
                : `Showing ${rangeStart}-${rangeEnd} of ${totalItems}`
            }}
          </p>
          <div class="ms-auto d-flex align-items-center gap-2">
            <span class="text-xs text-secondary">
              Page {{ currentPage }} of {{ totalPages }}
            </span>
            <div class="btn-group btn-group-sm">
              <button type="button" class="btn btn-outline-secondary" :disabled="!canGoPrev" @click="changePage(-1)">
                Previous
              </button>
              <button type="button" class="btn btn-outline-secondary" :disabled="!canGoNext" @click="changePage(1)">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showMediaModal && currentMediaUrl" class="modal fade show d-block" tabindex="-1"
      style="background: rgba(0, 0, 0, 0.75);" @click.self="closeMedia">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content bg-transparent border-0 shadow-none">
          <div class="modal-body text-center p-0">
            <img :src="currentMediaUrl" alt="media preview" class="img-fluid rounded" />
          </div>
          <div class="modal-footer justify-content-center border-0">
            <button type="button" class="btn btn-light" @click="closeMedia">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.assign-filter-dropdown {
  top: 100%;
  left: 0;
  z-index: 1000;
  min-width: 200px;
  max-height: 260px;
  overflow-y: auto;
}
</style>
